import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Split, X } from 'lucide-react';
import { divideBatch } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Planning a job into production runs.
 *
 * A batch is what the floor schedules against, but the only way to get more than
 * one used to be passing part of a stage and letting the leftover split off —
 * batches were an accident of production rather than a decision made before it.
 * This is the decision: 10,000 pcs as four runs of 2,500, cut while the job is
 * standing still.
 *
 * The sizes are shown as text and stay editable, so an even division is the
 * default without being the only option — a run that has to match a sheet count
 * or a machine's bed is typed over the top.
 */

export function evenSizes(total, count) {
  if (!count || count < 2 || count > total) return [];
  const base = Math.floor(total / count);
  const extra = total % count;
  return Array.from({ length: count }, (_, i) => base + (i < extra ? 1 : 0));
}

export function parseSizes(text) {
  return text
    .split(/[\s,+]+/)
    .filter(Boolean)
    .map(Number)
    .filter(n => Number.isFinite(n));
}

export default function DivideBatchStrip({ batch, onDone, onCancel }) {
  const total = batch.quantity || 0;
  const [count, setCount] = useState(2);
  const [sizesText, setSizesText] = useState(evenSizes(total, 2).join(', '));
  const [saving, setSaving] = useState(false);

  const sizes = useMemo(() => parseSizes(sizesText), [sizesText]);
  const sum = sizes.reduce((s, n) => s + n, 0);
  const balanced = sizes.length >= 2 && sum === total && sizes.every(n => n > 0);

  const setCountAndSizes = (raw) => {
    const next = parseInt(raw, 10);
    setCount(raw);
    const even = evenSizes(total, next);
    if (even.length) setSizesText(even.join(', '));
  };

  const submit = async () => {
    setSaving(true);
    try {
      await divideBatch(batch.id, { quantities: sizes });
      toast.success(`Batch #${batch.batch_number} divided into ${sizes.length} runs`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to divide the batch');
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="divide-batch-strip">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-brand-blue">
        Divide batch #{batch.batch_number} · {total.toLocaleString()} pcs
      </span>

      <label className="flex flex-col gap-0.5" style={{ width: '6rem' }}>
        <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">Runs</span>
        <input
          type="number"
          min={2}
          max={total}
          value={count}
          onChange={(e) => setCountAndSizes(e.target.value)}
          aria-label="Number of runs"
          className="h-7 rounded border border-slate-300 bg-white px-2 text-[12px] tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-blue"
        />
      </label>

      <label className="flex flex-col gap-0.5" style={{ width: '20rem' }}>
        <span className="text-[9px] font-medium uppercase tracking-wider text-slate-500">
          Quantities
        </span>
        <input
          type="text"
          value={sizesText}
          onChange={(e) => setSizesText(e.target.value)}
          aria-label="Run quantities"
          className="h-7 rounded border border-slate-300 bg-white px-2 text-[12px] tabular-nums focus:outline-none focus:ring-1 focus:ring-brand-blue"
        />
      </label>

      <Button
        size="sm"
        className="h-7 bg-brand-blue text-white hover:bg-brand-blue-dark"
        disabled={!balanced || saving}
        onClick={submit}
        data-testid="confirm-divide-batch-btn"
      >
        <Split className="mr-1 h-3.5 w-3.5" />
        {saving ? 'Dividing…' : 'Divide'}
      </Button>
      <Button size="sm" variant="ghost" className="h-7" onClick={onCancel} aria-label="Cancel divide">
        <X className="h-3.5 w-3.5" />
      </Button>

      <span className={`w-full text-[11px] ${balanced ? 'text-slate-500' : 'text-amber-700'}`}>
        {balanced
          ? `${sizes.length} runs standing at ${batch.current_stage_name}, each carrying the work already done.`
          : sizes.length < 2
            ? 'Give at least two quantities.'
            : `The runs add up to ${sum.toLocaleString()} — the batch holds ${total.toLocaleString()}.`}
      </span>
    </div>
  );
}
